/**
 * Centralized Generation Orchestration Hook
 *
 * Wraps useVideoGeneration + useGenerationProgression and owns the full pipeline:
 * credit check → upload → progression tracking → prompt enhancement → video generation → polling → error handling
 *
 * Used by HomePage and ImageToVideoPage. Lander1Page (which has unique localStorage
 * persistence, promise-based auth, modal mutex) will be migrated in a follow-up.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useVideoGeneration } from './useVideoGeneration';
import { useGenerationProgression } from './useGenerationProgression';
import { uploadImageToStorage } from '../services/storage';
import { updateAsset } from '../services/assets';
import { getCreditBalance, getCostForDuration } from '../services/credits';
import { parseCreditError, CreditError } from '../utils/creditErrors';
import { GenerationProgression, GenerationStage } from '../types/progression';
import type { User, VideoDuration } from '../types/api';

// --- Types ---

export type FlowPhase =
  | 'idle'
  | 'checking_credits'
  | 'uploading'
  | 'generating'
  | 'completed'
  | 'failed';

export type { CreditError } from '../utils/creditErrors';

export interface GenerationFlowState {
  phase: FlowPhase;
  progression: GenerationProgression;
  progress: number; // 0-100 from useVideoGeneration
  videoUrl: string | null;
  assetId: string | null;
  error: string | null;
  creditError: CreditError | null;
  isProcessing: boolean; // convenience: phase is uploading|generating
  isComplete: boolean; // convenience: phase is completed
}

export interface UseGenerationFlowOptions {
  userData: User | null;
  userEmail?: string;
  sourcePage?: 'lander-1' | 'image-to-video' | 'homepage' | 'i2v'; // Controls video URL selection
  onComplete?: (videoUrl: string) => void;
  onError?: (error: string | CreditError) => void;
  // Timeout for prompt enhancement (ms). Passed through to useVideoGeneration.
  promptEnhanceTimeoutMs?: number;
}

export interface UseGenerationFlowReturn {
  state: GenerationFlowState;
  // duration defaults to 5 — only homepage passes 10. Preserves i2v /
  // ImageToVideoPage / Lander-1 contract: they call without the arg.
  generate: (image: File | null, prompt: string, existingAssetId?: string, duration?: VideoDuration) => Promise<void>;
  reset: () => void;
  dismissCreditError: () => void;
  // Surface a credit error from an external pre-flight (e.g. ad-handoff claim
  // returning 402) so the shared InsufficientCreditsModal can render without
  // duplicating modal logic at the page level.
  reportCreditError: (err: CreditError) => void;
}

// --- SessionStorage keys managed by this hook ---
const SS_IMAGE_URL = 'uploaded_image_url';
const SS_IMAGE_PATH = 'uploaded_image_path';
const SS_ASSET_ID = 'uploaded_asset_id';

function clearUploadSessionStorage() {
  sessionStorage.removeItem(SS_IMAGE_URL);
  sessionStorage.removeItem(SS_IMAGE_PATH);
  sessionStorage.removeItem(SS_ASSET_ID);
}

// --- Hook ---

export function useGenerationFlow(
  options: UseGenerationFlowOptions
): UseGenerationFlowReturn {
  const { userData, userEmail, sourcePage, onComplete, onError, promptEnhanceTimeoutMs } = options;

  // --- Local state ---
  const [phase, setPhase] = useState<FlowPhase>('idle');
  const [assetId, setAssetId] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<CreditError | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prompt ref kept in sync so useVideoGeneration always has the latest
  const promptRef = useRef('');
  const [currentPrompt, setCurrentPrompt] = useState('');

  // Duration selected for the current generation. Defaults to 5 so that
  // any caller that doesn't pass it (i2v, ImageToVideoPage, Lander-1)
  // keeps today's behavior. Homepage sets this to 10 when the user
  // has the toggle on.
  const [currentDuration, setCurrentDuration] = useState<VideoDuration>(5);

  // Bridge: useVideoGeneration reads assetId reactively. We trigger startGeneration
  // via a ref+useEffect after setting assetId state.
  const shouldStartGenerationRef = useRef(false);

  // --- Progression hook ---
  const {
    progression,
    setStage,
    updateStageProgress,
    setError: setProgressionError,
    reset: resetProgression,
  } = useGenerationProgression({
    onComplete: () => {},
    onError: (err) => {
      if (err && !err.includes('Failed to fetch') && !err.includes('Video generation')) {
        console.error('❌ [useGenerationFlow] Progression tracking error:', err);
      }
    },
  });

  // --- Video generation hook ---
  const {
    status: videoStatus,
    progress: videoProgress,
    videoUrl,
    error: videoError,
    startGeneration: startVideoGeneration,
    reset: resetVideoGeneration,
  } = useVideoGeneration({
    assetId: assetId || '',
    prompt: currentPrompt,
    userEmail,
    userData,
    sourcePage,
    // Only forward 10 — never send 5 (which is the backend default).
    // Keeps the i2v / ImageToVideoPage / Lander-1 contract: their POST
    // body has no `duration` key at all.
    duration: currentDuration === 10 ? 10 : undefined,
    promptEnhanceTimeoutMs,
    onComplete: (url) => {
      setPhase('completed');
      clearUploadSessionStorage();
      onComplete?.(url);
    },
    onError: (err) => {
      console.error('❌ [useGenerationFlow] Video generation error:', err);

      const credit = parseCreditError(err);
      if (credit) {
        resetProgression();
        resetVideoGeneration();
        setCreditError(credit);
        setPhase('failed');
        onError?.(credit);
        return;
      }

      // Non-credit error
      resetProgression();
      const errorMsg = typeof err === 'string' ? err : (err as any)?.message || 'Video generation failed';
      setError(errorMsg);
      setPhase('failed');
      onError?.(errorMsg);
    },
    onStageChange: (stage: GenerationStage, message?: string) => {
      try {
        setStage(stage, 0, message);
      } catch (e) {
        console.error('❌ [useGenerationFlow] Progression tracking error (non-fatal):', e);
      }
    },
    onStageProgress: (_stage: GenerationStage, progress: number) => {
      try {
        updateStageProgress(progress);
      } catch (e) {
        console.error('❌ [useGenerationFlow] Progression progress error (non-fatal):', e);
      }
    },
  });

  // Watch videoError for credit errors embedded in the error string
  useEffect(() => {
    if (videoError && videoError.includes('insufficient_credits')) {
      const credit = parseCreditError(videoError);
      if (credit) {
        resetProgression();
        resetVideoGeneration();
        setCreditError(credit);
        setPhase('failed');
        onError?.(credit);
      }
    }
  }, [videoError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bridge: trigger startVideoGeneration when assetId is set and flag is true
  useEffect(() => {
    if (shouldStartGenerationRef.current && assetId && currentPrompt.trim()) {
      shouldStartGenerationRef.current = false;

      // Next tick to ensure all state has propagated
      setTimeout(async () => {
        try {
          // Set progression to prompt enhancement stage
          try {
            setStage('enhancing_prompt', 0, 'Preparing to enhance prompt...');
          } catch (e) {
            console.error('❌ [useGenerationFlow] Progression tracking error (non-fatal):', e);
          }

          // Persist prompt on asset
          if (assetId && currentPrompt.trim()) {
            try {
              await updateAsset(assetId, { prompt: currentPrompt.trim() });
              sessionStorage.removeItem('pending_prompt');
            } catch (e) {
              console.error('❌ [useGenerationFlow] Failed to update asset with prompt:', e);
              // Don't block generation
            }
          }

          setPhase('generating');
          await startVideoGeneration();
        } catch (err) {
          console.error('❌ [useGenerationFlow] Generation error in bridge useEffect:', err);

          const credit = parseCreditError(err);
          if (credit) {
            resetProgression();
            resetVideoGeneration();
            setCreditError(credit);
            setPhase('failed');
            onError?.(credit);
            return;
          }

          setProgressionError(err instanceof Error ? err.message : 'Generation failed');
          setPhase('failed');
        }
      }, 100);
    }
  }, [assetId, currentPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- generate() ---

  const generate = useCallback(
    async (image: File | null, prompt: string, existingAssetId?: string, duration: VideoDuration = 5) => {
      // Validate inputs
      if (!image && !existingAssetId) {
        console.error('❌ [useGenerationFlow] Image or existingAssetId required');
        return;
      }
      if (!prompt.trim() || prompt.trim().length < 5) {
        console.error('❌ [useGenerationFlow] Prompt is required (min 5 chars)');
        return;
      }

      // Store prompt + duration for downstream hooks
      const trimmedPrompt = prompt.trim();
      promptRef.current = trimmedPrompt;
      setCurrentPrompt(trimmedPrompt);
      setCurrentDuration(duration);

      // Reset errors
      setError(null);
      setCreditError(null);

      // --- Step 1: Credit check ---
      // Cost is derived from the selected duration. Server bootstrap
      // values win when present; falls back to typed constants.
      const requiredCredits = getCostForDuration(duration, userData);
      setPhase('checking_credits');
      if (userData?.supabase_user_id) {
        try {
          const { credits } = await getCreditBalance(userData.supabase_user_id);
          if (credits < requiredCredits) {
            const creditErr: CreditError = {
              current_credits: credits,
              required_credits: requiredCredits,
              message: `Insufficient credits for ${duration}s video generation`,
            };
            setCreditError(creditErr);
            setPhase('failed');
            onError?.(creditErr);
            return;
          }
        } catch {
          // Network error on credit check — silently proceed, server-side check will catch it
        }
      }

      // --- Step 2: Upload (if no existing asset) ---
      if (existingAssetId) {
        // Skip upload, use existing asset.
        // CRITICAL: set the bridge trigger ref BEFORE the async updateAsset call.
        // If we set it after the await, React may re-render during the await,
        // the bridge effect fires with shouldStartGenerationRef = false (not set yet),
        // does nothing, and never re-fires because the deps (assetId, currentPrompt)
        // don't change again. This caused the handoff flow to get permanently stuck
        // at "Enhancing Prompt." (bug fix)
        shouldStartGenerationRef.current = true;
        setAssetId(existingAssetId);

        // Set progression to prompt enhancement stage
        try {
          setStage('enhancing_prompt', 0, 'Preparing to enhance prompt...');
        } catch (e) {
          console.error('❌ [useGenerationFlow] Progression tracking error (non-fatal):', e);
        }

        // Persist prompt on asset (non-blocking — bridge effect also does this)
        try {
          await updateAsset(existingAssetId, { prompt: trimmedPrompt });
          sessionStorage.removeItem('pending_prompt');
        } catch (e) {
          console.error('❌ [useGenerationFlow] Failed to update asset with prompt:', e);
        }

        setPhase('generating');
        return;
      }

      // Upload image
      setPhase('uploading');
      try {
        setStage('uploading_image', 0, 'Uploading image...');
      } catch (e) {
        console.error('❌ [useGenerationFlow] Progression tracking error (non-fatal):', e);
      }

      try {
        try { updateStageProgress(50); } catch { /* non-fatal */ }

        const uploadResult = await uploadImageToStorage(image!, undefined, trimmedPrompt);

        // Track storage stages
        try {
          setStage('storing_image_s3', 100, 'Image stored in S3');
          setTimeout(() => {
            try {
              setStage('storing_image_db', 100, 'Image record saved');
            } catch { /* non-fatal */ }
          }, 300);
        } catch { /* non-fatal */ }

        const uploadedAssetId = uploadResult.asset.id;

        // Store in sessionStorage
        sessionStorage.setItem(SS_IMAGE_URL, uploadResult.url);
        sessionStorage.setItem(SS_IMAGE_PATH, uploadResult.path);
        sessionStorage.setItem(SS_ASSET_ID, uploadedAssetId);

        // Trigger generation via bridge useEffect
        shouldStartGenerationRef.current = true;
        setAssetId(uploadedAssetId);
      } catch (err) {
        console.error('❌ [useGenerationFlow] Upload failed:', err);
        setProgressionError(err instanceof Error ? err.message : 'Upload failed');
        setError(err instanceof Error ? err.message : 'Upload failed');
        setPhase('failed');
      }
    },
    [userData, onError, setStage, updateStageProgress, setProgressionError] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // --- reset() ---

  const reset = useCallback(() => {
    setPhase('idle');
    setAssetId(null);
    setError(null);
    setCreditError(null);
    setCurrentPrompt('');
    promptRef.current = '';
    shouldStartGenerationRef.current = false;
    resetVideoGeneration();
    resetProgression();
    clearUploadSessionStorage();
  }, [resetVideoGeneration, resetProgression]);

  // --- dismissCreditError() ---

  const dismissCreditError = useCallback(() => {
    setCreditError(null);
    // Don't reset phase — let the page decide what to do next
  }, []);

  // --- reportCreditError() ---
  // Lets callers surface an externally-detected credit error (e.g. a 402 from
  // the ad-handoff claim endpoint) through the same state path the internal
  // credit check uses. Keeps modal rendering logic in one place.

  const reportCreditError = useCallback((err: CreditError) => {
    resetProgression();
    setCreditError(err);
    setPhase('failed');
  }, [resetProgression]);

  // --- Build state ---

  const state: GenerationFlowState = {
    phase,
    progression,
    progress: videoProgress,
    videoUrl,
    assetId,
    error,
    creditError,
    isProcessing: phase === 'uploading' || phase === 'generating',
    isComplete: phase === 'completed',
  };

  return {
    state,
    generate,
    reset,
    dismissCreditError,
    reportCreditError,
  };
}
